import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { formatNumber } from "@/lib/format";
import { FileText, Truck, Boxes, ScrollText, TrendingUp, Factory, AlertTriangle, ArrowRight } from "lucide-react";
import { StockMonthlyChart } from "@/components/StockMonthlyChart";

/**
 * Iter v34aw · Full-Bento — Batching Dashboard
 */
const TILES = [
  { to: "/batching/po", icon: FileText, title: "PO Vendor", desc: "Buat & kirim PO untuk supplier (Beton, semen, dll)", color: "blue" },
  { to: "/batching/sj", icon: Truck, title: "Surat Jalan", desc: "Cetak Surat Jalan Readymix siap antar", color: "emerald" },
  { to: "/batching/stock", icon: Boxes, title: "Stok Material", desc: "Lihat stok bahan baku", color: "amber" },
  { to: "/batching/input-material", icon: ScrollText, title: "Input Material", desc: "Konversi kubik ↔ kg", color: "stone" },
  { to: "/batching/rekap", icon: TrendingUp, title: "Rekap Readymix", desc: "Total mobil & kubikasi keluar", color: "blue" },
];

const COLOR_CLASSES = {
  blue: "bg-blue-50 text-blue-700 border-blue-100",
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
  amber: "bg-amber-50 text-amber-700 border-amber-100",
  stone: "bg-stone-100 text-stone-700 border-stone-200",
};

export default function BatchingDashboard() {
  const [alerts, setAlerts] = useState({ count: 0, alerts: [] });
  const [consumption, setConsumption] = useState({ totals: {}, materials: [] });

  useEffect(() => {
    api.get("/batching/stock-alerts").then((r) => setAlerts(r.data || { count: 0, alerts: [] })).catch(() => {});
    api.get("/batching/consumption-stats", { params: { window_days: 30 } })
      .then((r) => setConsumption(r.data || { totals: {}, materials: [] }))
      .catch(() => {});
  }, []);

  const totalKg = consumption.totals?.total_kg || 0;
  const avgDaily = consumption.totals?.avg_daily_kg || 0;

  return (
    <div className="min-h-full bg-[#f6f5f0]" data-testid="batching-dashboard">
      <div className="max-w-[1600px] mx-auto p-4 md:p-6 lg:p-8 space-y-6">
        {/* Header */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-stone-500 mb-1.5 font-semibold">Modul · Batching Plant</div>
          <h1 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight text-stone-900">Dashboard Batching</h1>
          <p className="text-stone-500 mt-1 text-sm">Manajemen ready-mix concrete plant — PO supplier, Surat Jalan proyek, dan rekap pengiriman.</p>
        </div>

        {/* Bento hero */}
        <div className="grid grid-cols-12 gap-4">
          {/* Production hero */}
          <div className="col-span-12 md:col-span-6 rounded-3xl bg-gradient-to-br from-blue-700 via-blue-800 to-emerald-800 text-white p-8 relative overflow-hidden">
            <div className="absolute -right-16 -top-16 w-72 h-72 rounded-full bg-emerald-400/20 blur-3xl"></div>
            <div className="absolute right-6 bottom-6 opacity-10">
              <Factory className="w-40 h-40"/>
            </div>
            <div className="relative">
              <div className="text-[11px] uppercase tracking-widest text-blue-200 mb-3">Konsumsi Material 30 Hari</div>
              <div className="font-display text-5xl md:text-6xl tracking-tight">
                {formatNumber(totalKg)} <span className="text-2xl text-blue-200">kg</span>
              </div>
              <div className="mt-2 text-sm text-blue-100">Avg <b>{formatNumber(avgDaily)} kg/hari</b> · {consumption.materials?.length || 0} material aktif</div>
              <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-white/10">
                <div>
                  <div className="text-[10px] uppercase text-blue-200 tracking-wider">Alert Stok</div>
                  <div className="text-2xl font-mono-num mt-1 text-white">{alerts.count}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-blue-200 tracking-wider">Material</div>
                  <div className="text-2xl font-mono-num mt-1 text-white">{consumption.materials?.length || 0}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-blue-200 tracking-wider">Modul</div>
                  <div className="text-2xl font-mono-num mt-1 text-white">{TILES.length}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Alerts + quick module tiles side */}
          <div className="col-span-12 md:col-span-6 space-y-4">
            {alerts.count > 0 ? (
              <div className="rounded-2xl bg-red-50/60 border border-red-200 p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-600"/>
                    <div className="font-semibold text-red-800">Alert Stok Minim</div>
                  </div>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-semibold">{alerts.count}</span>
                </div>
                <div className="space-y-1.5 max-h-32 overflow-y-auto">
                  {alerts.alerts.slice(0, 5).map((a) => (
                    <div key={a.id} className="flex items-center justify-between text-xs">
                      <span className="font-medium text-stone-800">{a.name}</span>
                      <span className="font-mono-num text-red-700">−{formatNumber(a.short_kg)} kg</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="rounded-2xl bg-emerald-50/60 border border-emerald-100 p-5">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
                  <div className="font-semibold text-emerald-800">Semua Stok Aman</div>
                </div>
                <p className="text-xs text-emerald-700 mt-1">Tidak ada material di bawah batas minimum.</p>
              </div>
            )}

            {/* Fast link tile */}
            <Link to="/batching/sj" className="block rounded-2xl bg-white border border-stone-200/70 p-5 hover:shadow-md transition group">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Aksi Cepat</div>
                  <div className="font-display text-xl mt-1 text-stone-900">Buat Surat Jalan Readymix</div>
                </div>
                <div className="w-10 h-10 rounded-full bg-stone-900 text-white flex items-center justify-center group-hover:bg-emerald-600 transition">
                  <ArrowRight className="w-4 h-4"/>
                </div>
              </div>
            </Link>
          </div>
        </div>

        {/* Iter v34dc — Rekap stok bulanan (masuk vs keluar) */}
        <StockMonthlyChart />

        {/* Module tiles */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {TILES.map((t) => (
            <Link key={t.to} to={t.to} className="group block rounded-2xl bg-white border border-stone-200/70 p-5 hover:shadow-md hover:border-stone-300 transition-all">
              <div className={`w-12 h-12 rounded-xl border flex items-center justify-center mb-3 ${COLOR_CLASSES[t.color]}`}>
                <t.icon className="h-5 w-5" />
              </div>
              <div className="flex items-center justify-between">
                <div className="font-display text-lg font-semibold text-stone-900">{t.title}</div>
                <ArrowRight className="w-4 h-4 text-stone-300 group-hover:text-stone-900 transition"/>
              </div>
              <p className="text-xs text-stone-500 mt-1 leading-relaxed">{t.desc}</p>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
